import { isGroupRow } from '../../stores/utils/group-row-guards';
import { resolveBobcatAccess } from '../../utils/bobcat-flags';
import {
  agentCategoryFor,
  type AgentCategory,
} from '../../utils/agent-palette';
import {
  UNGROUPED_SECTION_ID,
  UNGROUPED_SECTION_LABEL,
  labelFor,
} from '../../stores/utils/ungrouped-rows';
// TYPE-ONLY, same reason `ungrouped-rows.ts` imports it this way: pulling
// the runtime `NodeColumns` enum drags the whole store (and the Lit design
// system through it) into this module, which breaks node-env unit tests.
import type { AgentSource, TableNode } from '../../stores/table-store';

/**
 * Build Stage canvas, Phase 1 — the data this phase needs and nothing more.
 *
 * Deliberately does not build the full per-node SECTION projection the way
 * Agent View's now-deleted model did (removed 2026-09-23, once canvas
 * reached feature parity with it — see `project_bobcat_engineering_branches`
 * memory) — the canvas renders one card per GROUP, not per node, so pulling
 * that machinery in for fields it does not need would make this module
 * depend on a shape it has no use for.
 *
 * `CanvasAgentNode.members` (added Phase 3 build A) is NOT that projection:
 * it is just each group's real member rows (id/number/label), the minimum a
 * click target needs. It carries no per-node type/routing/section data —
 * Phase 3 build B owns the full expanded-row visual design (node-type dots,
 * etc.); this only exists so a node can be clicked at all.
 *
 * Phase 1 shows only REAL, persisted groups (`nodeType === 'g'`) — no
 * proposed/auto-grouping preview. A solution with no groups yet renders an
 * empty canvas; grouping it in Table View (the "Group by agent" action,
 * `applyAutoAgentGrouping`) is what populates this view. Keeping this phase
 * to "the same source of truth Table View already treats as ground truth"
 * avoids re-deriving `planAutoAgentGroups` logic a second place.
 *
 * A real Use Case Agent group needs no special handling: `buildCanvasAgentNodes`
 * below renders every real group row identically regardless of name, so
 * Supervisor Agent, Knowledge Agent, and any other named group (a Use Case
 * Agent, e.g. "File a Claim") all render the same way, for free.
 *
 * REVISED 2026-09-23 (Elana): this module used to also build an aggregate
 * "Use-case Agents" card over ungrouped action rows, reusing a synthetic
 * bucket Agent View's model used to build. That read Use Case Agents as
 * leftover actions rather than real groups, which was wrong: a Use Case
 * Agent is a real group, same as Supervisor or Knowledge Agent, just a third
 * named category — nothing with no group at all is a use case agent, it is
 * simply ungrouped. `buildUngroupedSummary` below replaces it: a plain
 * count, not a card, matching what "ungrouped" actually means everywhere
 * else in this codebase.
 */

export const UNTITLED_GROUP_LABEL = 'Untitled group';

/**
 * PSTUDIO-4632: canvas editing needs its flag AND rows writable now
 * (`tableStore.canWriteRows`: an editor, not mid-publish, in `editing`). Every
 * canvas edit ends in `insertRowBelow`, which unlocks a table a publish has
 * locked.
 */
/**
 * PSTUDIO-4632: dropping a Resource Library card onto an agent card writes rows
 * (`addResourceLibRowsToGroup` ends in `insertRowBelow`), so it needs its flag
 * AND rows writable now, like canvas editing.
 */
export const canvasDropEnabled = (
  featureFlags: Record<string, boolean> | undefined,
  store: { canWriteRows: boolean }
): boolean =>
  resolveBobcatAccess('fiveStageNav', featureFlags) === 'allowed' &&
  store.canWriteRows;

export const canvasEditingEnabled = (
  featureFlags: Record<string, boolean> | undefined,
  store: { canWriteRows: boolean }
): boolean =>
  resolveBobcatAccess('fiveStageNav', featureFlags) === 'allowed' &&
  store.canWriteRows;

/**
 * Phase 3 build B — the sidebar's "Core" vs "Use-case agents" split. `core`
 * is exactly the two names `auto-agent-grouping.ts` creates (`AUTO_GROUP_NAMES`:
 * "Supervisor Agent", "Knowledge Agent") — real, structural, the same list
 * that module itself treats as the complete Core set. Everything else is
 * `use-case`, INCLUDING a real Use Case Agent group and an untitled group —
 * there is no third bucket; per `reference_use_case_agents_category`, a Use
 * Case Agent is just a third named category of real group, not special-cased
 * data.
 */
export type CanvasAgentCategory = AgentCategory;

// The Core/Use-case split lives with the agent palette so every surface that
// draws an agent agrees on it.
const categoryFor = (name: string): CanvasAgentCategory =>
  agentCategoryFor(name);

export interface CanvasAgentNodeMember {
  /** The member row's id — what Phase 3 build A's click handler selects. */
  id: string;
  nodeNumber?: TableNode['nodeNumber'];
  label: string;
  /**
   * Phase 3 build B: which node-type dot to draw on this row. Reuses the
   * same D/A classification `node-type-badge.ts` already gives Table View's
   * grid and `NodePropertyPanel` — no new taxonomy invented here.
   */
  nodeType: TableNode['nodeType'];
}

export interface CanvasAgentNode {
  /** The group row's id — also the React Flow node id. */
  id: string;
  title: string;
  /** `settings.groupColor`, or `null` when the user has not picked one. */
  color: string | null;
  nodeCount: number;
  /** Phase 3 build B — see `CanvasAgentCategory`'s doc comment. */
  category: CanvasAgentCategory;
  /**
   * The group's real member rows, in `subRows` order. Phase 1 only read
   * `subRows.length`; Phase 3 build A (node click → detail panel) needs the
   * actual rows as click targets. A `subRows` entry naming a row that is not
   * in `rows` is skipped rather than rendered as a broken target.
   *
   * Deliberately does NOT exclude a member whose own `hidden` is true — found
   * live, not assumed: a member row's `hidden` flag means its parent group is
   * COLLAPSED (`table-store.ts`: "a subRow is hidden iff its parent group is
   * collapsed"), not pending-delete — that meaning is specific to a GROUP
   * HEADER row (see this function's own doc comment). Every group this
   * session's features create starts collapsed (`planAutoAgentGroups`,
   * `planUseCaseAgentGroups`), so filtering on it here would have hidden
   * every fresh group's members — confirmed live against "Harmony Music
   * Store"'s three (collapsed) Use-Case-Agent groups.
   */
  members: CanvasAgentNodeMember[];
  /** Where the agent came from, when Create built it (`AgentSource`). */
  source?: AgentSource;
}

/**
 * One entry per visible group row, in row order. A hidden group header is a
 * pending delete — a collapsed group keeps its header visible, only a group
 * actually being deleted hides it — so it is excluded here too.
 */
export const buildCanvasAgentNodes = (rows: TableNode[]): CanvasAgentNode[] => {
  const rowById = new Map(rows.map((row) => [row.id, row]));

  return rows
    .filter((row) => isGroupRow(row) && row.hidden !== true)
    .map((row) => {
      const name = row.settings?.groupName;
      const subRowIds = row.settings?.subRows ?? [];
      const members: CanvasAgentNodeMember[] = subRowIds
        .map((id) => rowById.get(id))
        .filter((member): member is TableNode => member !== undefined)
        .map((member) => ({
          id: member.id,
          nodeNumber: member.nodeNumber,
          label: labelFor(member),
          nodeType: member.nodeType,
        }));

      const trimmedName = typeof name === 'string' ? name.trim() : '';

      return {
        id: row.id,
        title: trimmedName || UNTITLED_GROUP_LABEL,
        color: row.settings?.groupColor ?? null,
        nodeCount: subRowIds.length,
        category: categoryFor(trimmedName),
        members,
        ...(row.settings?.agentSource
          ? { source: row.settings.agentSource }
          : {}),
      };
    });
};

/** The plain-text "N ungrouped" indicator's id — `ungrouped-rows.ts`'s
 *  shared section id. */
export const UNGROUPED_SUMMARY_ID = UNGROUPED_SECTION_ID;

export interface CanvasUngroupedSummary {
  id: string;
  title: string;
  count: number;
}

/**
 * Every ungrouped row, in row order — the one traversal `buildUngroupedSummary`
 * (a count) and `buildUngroupedNodes` (the same rows, projected for display)
 * both build on, so the "what counts as ungrouped" rule lives in exactly one
 * place. An orphaned `groupId` (naming a group that no longer exists) reads
 * as ungrouped rather than being dropped — a node silently disappearing from
 * every view would be worse than showing it in the wrong place. This needs
 * no "collapsed vs. pending-delete" split the way a row's table position
 * would: this is never rendered at a row's table position, so there is no
 * "hidden because its group is collapsed" case to tell apart from "hidden
 * because it is mid-delete" — any `hidden === true` row is simply excluded.
 */
const collectUngroupedRows = (rows: TableNode[]): TableNode[] => {
  const realGroupIds = new Set(
    rows
      .filter((row) => isGroupRow(row) && row.hidden !== true)
      .map((row) => row.id)
  );

  return rows.filter((row) => {
    if (isGroupRow(row) || row.hidden === true) return false;

    const groupId = row.settings?.groupId;
    const belongsToRealGroup = groupId != null && realGroupIds.has(groupId);
    return !belongsToRealGroup;
  });
};

export const buildUngroupedSummary = (
  rows: TableNode[]
): CanvasUngroupedSummary | null => {
  const count = collectUngroupedRows(rows).length;
  if (count === 0) return null;

  return {
    id: UNGROUPED_SUMMARY_ID,
    title: UNGROUPED_SECTION_LABEL,
    count,
  };
};

export interface CanvasUngroupedNode {
  id: string;
  nodeNumber?: TableNode['nodeNumber'];
  label: string;
}

/**
 * The actual ungrouped nodes, for the expanded "show ungrouped" state — see
 * `showUngroupedNodes` on `tableStore` (until 2026-09-23, `Agent View`'s own
 * Ungrouped card read the same preference; canvas is now its only
 * consumer).
 */
export const buildUngroupedNodes = (rows: TableNode[]): CanvasUngroupedNode[] =>
  collectUngroupedRows(rows).map((row) => ({
    id: row.id,
    nodeNumber: row.nodeNumber,
    label: labelFor(row),
  }));

/**
 * Project Bobcat (agent templates) — where an agent came from, in
 * words: the badge's tooltip on the canvas card and the line on Design's
 * agent card. `todo` is what is still to connect, when anything is.
 */
export const describeAgentSource = (
  source: AgentSource
): { label: string; todo: string | null } => {
  const todo = source.connect?.length
    ? `Still to connect: ${source.connect.join('; ')}.`
    : null;
  if (source.kind === 'template' && source.status === 'drafting') {
    return {
      label: `Building from the ${source.template ?? ''} template…`.replace(
        '  ',
        ' '
      ),
      todo: null,
    };
  }
  if (source.kind === 'template') {
    return {
      label: `From the ${source.template ?? ''} template`.replace('  ', ' '),
      todo,
    };
  }
  if (source.status === 'drafting') {
    return { label: 'Drafting with AI…', todo: null };
  }
  if (source.status === 'failed') {
    return {
      label: 'AI couldn’t draft this agent',
      todo: 'Fill it in with Ask AI.',
    };
  }
  return { label: 'Drafted by AI', todo };
};
